"use client";

import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AttachedFileMetadata } from "@/types/command-agent";
import {
  buildBlobDownloadHref,
  getFileTypeLabel,
  isAzureBlobUrl,
  sanitizeDownloadFilename,
} from "@/utils/download-filename";

interface MessageAttachmentsProps {
  files: AttachedFileMetadata[];
  className?: string;
  /** Compact chips for the sidebar command panel */
  compact?: boolean;
}

export function MessageAttachments({
  files,
  className,
  compact = false,
}: MessageAttachmentsProps) {
  if (!files.length) return null;

  return (
    <div className={cn("flex flex-wrap gap-2 justify-end", className)}>
      {files.map((file) => {
        const typeLabel = getFileTypeLabel(file.mime_type, file.file_name);
        const isHtml = typeLabel === "HTML";
        const displayName = sanitizeDownloadFilename(file.file_name);
        const href = file.file_url
          ? isAzureBlobUrl(file.file_url)
            ? buildBlobDownloadHref(file.file_url, displayName)
            : file.file_url
          : undefined;

        return (
          <a
            key={file.id}
            href={href}
            download={href ? displayName : undefined}
            target={href && !isAzureBlobUrl(file.file_url) ? "_blank" : undefined}
            rel="noopener noreferrer"
            className={cn(
              "group/file flex items-center gap-3 rounded-xl border bg-background/80 backdrop-blur-sm",
              "border-border/60 shadow-sm",
              "hover:border-primary/40 hover:shadow-md hover:bg-accent/30 transition-all duration-200",
              "text-left no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              compact
                ? "min-w-[140px] max-w-[200px] gap-2 px-2.5 py-2"
                : "min-w-[180px] max-w-[260px] px-3.5 py-2.5"
            )}
          >
            <span
              className={cn(
                "flex shrink-0 items-center justify-center rounded-lg",
                compact ? "size-8" : "size-10",
                isHtml
                  ? "bg-orange-500/10 text-orange-600 dark:text-orange-400"
                  : "bg-primary/10 text-primary"
              )}
            >
              <FileText className={compact ? "size-4" : "size-5"} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span
                className={cn(
                  "truncate font-medium leading-tight text-foreground",
                  compact ? "text-xs" : "text-sm"
                )}
                title={displayName}
              >
                {displayName}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {typeLabel} File
              </span>
            </span>
          </a>
        );
      })}
    </div>
  );
}
